import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "src/Utils/API";
import {
  DeclarePaymentPayload,
  Payment,
} from "src/business/payment/payment";
import { paymentErrorFrom } from "src/business/payment/paymentErrors";

/**
 * The staff payment endpoints answer in the `{ data }` envelope, but every
 * refusal is a Spring `ResponseStatusException` — an error body, not an
 * envelope with `success: false`. So `response.ok` is checked first and the
 * status carries the meaning; only then is the envelope unwrapped.
 */
type ApiResponseV2<T> = {
  data: T;
};

export const paymentKeys = {
  history: (restaurantId: string) =>
    ["payments", "history", restaurantId] as const,
  current: (restaurantId: string) =>
    ["payments", "current", restaurantId] as const,
};

const paymentsUrl = (restaurantId: string) =>
  `${API_URL}/restaurants/${restaurantId}/payments`;

const readPayment = async (response: Response): Promise<Payment> => {
  if (!response.ok) {
    throw paymentErrorFrom(response.status);
  }
  const body: ApiResponseV2<Payment> = await response.json();
  return body.data;
};

const readPayments = async (response: Response): Promise<Payment[]> => {
  if (!response.ok) {
    throw paymentErrorFrom(response.status);
  }
  const body: ApiResponseV2<Payment[]> = await response.json();
  return body.data;
};

/**
 * Both keys after every mutation: declaring, replacing a proof and cancelling
 * all change the pending payment *and* the history row that mirrors it.
 */
const usePaymentInvalidation = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: paymentKeys.history(restaurantId),
    });
    queryClient.invalidateQueries({
      queryKey: paymentKeys.current(restaurantId),
    });
  };
};

// GET /web/restaurants/{restaurantId}/payments
export const usePaymentHistory = (restaurantId: string) =>
  useQuery({
    queryKey: paymentKeys.history(restaurantId),
    queryFn: async (): Promise<Payment[]> =>
      readPayments(
        await fetch(paymentsUrl(restaurantId), { credentials: "include" }),
      ),
    enabled: !!restaurantId,
  });

// GET /web/restaurants/{restaurantId}/payments/current
// A 404 means nothing is awaiting review — the normal, declarable state, not an
// error, so it resolves to `null`.
export const useCurrentPayment = (restaurantId: string) =>
  useQuery({
    queryKey: paymentKeys.current(restaurantId),
    queryFn: async (): Promise<Payment | null> => {
      const response = await fetch(`${paymentsUrl(restaurantId)}/current`, {
        credentials: "include",
      });
      if (response.status === 404) {
        return null;
      }
      return readPayment(response);
    },
    enabled: !!restaurantId,
  });

// POST /web/restaurants/{restaurantId}/payments — multipart/form-data
// No Content-Type header is set: the browser has to add the multipart boundary
// itself, and setting it by hand produces a body the backend cannot parse.
export const useDeclarePayment = (restaurantId: string) => {
  const invalidate = usePaymentInvalidation(restaurantId);

  return useMutation({
    mutationFn: async ({
      proof,
      type,
      targetTier,
      period,
    }: DeclarePaymentPayload): Promise<Payment> => {
      const formData = new FormData();
      formData.append("proof", proof);
      formData.append("type", type);
      if (targetTier) {
        formData.append("targetTier", targetTier);
      }
      // Omitted, the backend defaults to MONTHLY; sent, it decides both the
      // months the subscription gains and the price it is charged at.
      if (period) {
        formData.append("period", period);
      }

      return readPayment(
        await fetch(paymentsUrl(restaurantId), {
          method: "POST",
          credentials: "include",
          body: formData,
        }),
      );
    },
    onSuccess: invalidate,
  });
};

// PUT /web/restaurants/{restaurantId}/payments/{paymentId}/proof
export const useReplaceProof = (restaurantId: string) => {
  const invalidate = usePaymentInvalidation(restaurantId);

  return useMutation({
    mutationFn: async ({
      paymentId,
      proof,
    }: {
      paymentId: string;
      proof: File;
    }): Promise<Payment> => {
      const formData = new FormData();
      formData.append("proof", proof);

      return readPayment(
        await fetch(`${paymentsUrl(restaurantId)}/${paymentId}/proof`, {
          method: "PUT",
          credentials: "include",
          body: formData,
        }),
      );
    },
    onSuccess: invalidate,
  });
};

// DELETE /web/restaurants/{restaurantId}/payments/{paymentId}
// Cancels rather than deletes: the payment stays in the history as CANCELLED.
export const useCancelPayment = (restaurantId: string) => {
  const invalidate = usePaymentInvalidation(restaurantId);

  return useMutation({
    mutationFn: async (paymentId: string): Promise<void> => {
      const response = await fetch(
        `${paymentsUrl(restaurantId)}/${paymentId}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok) {
        throw paymentErrorFrom(response.status);
      }
    },
    onSuccess: invalidate,
  });
};

/**
 * GET /web/restaurants/{restaurantId}/payments/{paymentId}/proof
 *
 * A plain async function, not a hook: a proof is an action with a side effect,
 * has nothing to cache and must not be refetched on window focus. The bytes
 * come back raw, and the content type is whatever the backend resolved from the
 * file's magic bytes — not anything the client remembered.
 */
export const downloadProof = async (
  restaurantId: string,
  paymentId: string,
): Promise<{ blob: Blob; contentType: string | null }> => {
  const response = await fetch(
    `${paymentsUrl(restaurantId)}/${paymentId}/proof`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw paymentErrorFrom(response.status);
  }
  return {
    blob: await response.blob(),
    contentType: response.headers.get("Content-Type"),
  };
};
